import { isDataTypeLoaded } from '@medplum/core'
import type { Resource, ResourceType } from '@medplum/fhirtypes'
import {
  type BundleToChangeSetsInput,
  bundleResourceTypes,
  bundleToChangeSets,
} from '@seen/core/queued-edits/bundle-to-change-sets'
import type { ResourceChangeSet } from '@seen/core/queued-edits/model'
import { bundleBaseRefs } from '@seen/core/queued-edits/proposal-drift'
import { parseHistoryReferenceString } from '@seen/core/references'
import { useQueries } from '@tanstack/react-query'
import { useMemo } from 'react'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useMemoValue } from '#src/hooks/useMemoValue.ts'
import { useQuery } from '#src/hooks/useQuery.ts'

export interface UseBundleChangeSetsResult {
  // Change sets still under review — already-applied entries are split out.
  changes: ResourceChangeSet[]
  // Entries whose proposed change is already present in current state (PATCH
  // no-op beyond the origin tag, DELETE target absent, conditional create
  // whose predicate already matches). The review surface drops these from the
  // decidable list and surfaces the count.
  alreadyApplied: ResourceChangeSet[]
  isLoading: boolean
  error: Error | undefined
}

const EMPTY_RESULT: UseBundleChangeSetsResult = {
  changes: [],
  alreadyApplied: [],
  isLoading: false,
  error: undefined,
}

// Compute queued-edits change sets from a proposed Bundle. The schema-aware
// differ needs every involved resource type's schema in globalSchema, so this
// loads them via `medplum.requestSchema` first and only then runs
// `bundleToChangeSets`. Conditional-create (POST + ifNoneExist) entries also
// need their predicate evaluated server-side — one search per entry — to
// classify "already exists".
export function useBundleChangeSets(
  input: BundleToChangeSetsInput | undefined
): UseBundleChangeSetsResult {
  const medplum = useMedplum()
  const memoInput = useMemoValue(input)
  const resourceTypes = useMemo(
    () =>
      memoInput ? bundleResourceTypes(memoInput.bundle, memoInput.current) : [],
    [memoInput]
  )

  const schemaQuery = useQuery({
    queryKey: ['queued-edits-schemas', resourceTypes],
    enabled: resourceTypes.length > 0,
    staleTime: Infinity,
    queryFn: async () => {
      // requestSchema can reject on parts we don't need (e.g. the mock
      // client's search-parameter GraphQL lookup) while still populating the
      // base schema — isDataTypeLoaded is the real gate.
      await Promise.allSettled(
        resourceTypes.map((rt) => medplum.requestSchema(rt))
      )
      const missing = resourceTypes.filter((rt) => !isDataTypeLoaded(rt))
      if (missing.length > 0) {
        throw new Error(`Could not load schema for ${missing.join(', ')}`)
      }
      return true
    },
  })

  // Conditional creates: whether a resource matching each entry's ifNoneExist
  // predicate already exists, keyed by the entry's fullUrl.
  const conditionalCreates = useMemo(
    () =>
      (memoInput?.bundle.entry ?? []).flatMap((entry) => {
        const { method, url, ifNoneExist } = entry.request ?? {}
        return method === 'POST' && url && ifNoneExist && entry.fullUrl
          ? [{ fullUrl: entry.fullUrl, resourceType: url, ifNoneExist }]
          : []
      }),
    [memoInput]
  )
  const conditionalQueries = useQueries({
    queries: conditionalCreates.map((entry) => ({
      queryKey: [
        'queued-edits-conditional-create',
        entry.resourceType,
        entry.ifNoneExist,
      ],
      queryFn: async () =>
        Boolean(
          await medplum.searchOne(
            entry.resourceType as ResourceType,
            entry.ifNoneExist
          )
        ),
    })),
  })
  const conditionalPending = conditionalQueries.some((q) => q.isPending)
  const conditionalKey = conditionalQueries
    .map((q) => String(q.data ?? ''))
    .join(',')

  // The pinned base versions the fhirpath-patch entries were drafted against
  // (`__seenBaseRef`) — loaded to drive the changed-since-proposed drift flag.
  // Versions are immutable, so cache forever. A failed read (purged history)
  // just skips the drift check for that entry.
  const baseRefs = useMemo(
    () => (memoInput ? bundleBaseRefs(memoInput.bundle.entry ?? []) : []),
    [memoInput]
  )
  const baseQueries = useQueries({
    queries: baseRefs.map((baseRef) => {
      const { resourceType, id, versionId } =
        parseHistoryReferenceString(baseRef)
      return {
        // readVersion-keyed so the cache fan-out (cacheIndividualResources)
        // skips these: they return OLD pinned versions, which must never
        // overwrite the live read-cache entries the `current` map is built
        // from.
        queryKey: cacheKeys.readVersion(
          resourceType as ResourceType,
          id,
          versionId,
          'Resource'
        ),
        staleTime: Infinity,
        retry: false,
        queryFn: async () =>
          medplum.readVersion(resourceType as ResourceType, id, versionId),
      }
    }),
  })
  const basePending = baseQueries.some((q) => q.isPending)
  const baseKey = baseQueries
    .map((q) => (q.data ? 'y' : q.isError ? 'e' : ''))
    .join(',')

  // conditionalKey is a content hash of the useQueries results (the array has a fresh identity every render) and stands in for conditionalQueries[…].data
  return useMemo(() => {
    if (!memoInput || resourceTypes.length === 0) {
      return EMPTY_RESULT
    }
    if (!schemaQuery.data || conditionalPending || basePending) {
      return {
        ...EMPTY_RESULT,
        isLoading: schemaQuery.isPending || conditionalPending || basePending,
        error: schemaQuery.error ?? undefined,
      }
    }
    try {
      const conditionalCreateExists = Object.fromEntries(
        conditionalCreates.map((entry, index) => [
          entry.fullUrl,
          conditionalQueries[index]?.data === true,
        ])
      )
      const bases: Record<string, Resource> = {}
      baseRefs.forEach((baseRef, index) => {
        const data = baseQueries[index]?.data
        if (data) {
          bases[baseRef] = data
        }
      })
      const all = bundleToChangeSets({
        ...memoInput,
        conditionalCreateExists,
        bases,
      })
      return {
        changes: all.filter((c) => !c.alreadyApplied),
        alreadyApplied: all.filter((c) => c.alreadyApplied),
        isLoading: false,
        error: undefined,
      }
    } catch (error) {
      return {
        ...EMPTY_RESULT,
        error: error instanceof Error ? error : new Error(String(error)),
      }
    }
    // conditionalKey/baseKey stand in for the useQueries result arrays (fresh
    // identity every render) and capture their per-query data changes.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [
    memoInput,
    resourceTypes,
    schemaQuery.data,
    schemaQuery.isPending,
    schemaQuery.error,
    conditionalCreates,
    conditionalPending,
    conditionalKey,
    baseRefs,
    basePending,
    baseKey,
  ])
}
